import { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Button, EmptyState } from "@/admin/ui";
import { StatusPill, PriorityPill } from "@/user/workspace/wsui";
import { fmtDate, fmtBytes, downloadAttachment } from "@/user/workspace/ws.api";
import { ceoApi } from "@/user/ceo/ceoApi";

export default function CeoDepartmentDetail() {
    const { dept } = useParams();
    const nav = useNavigate();
    useOutletContext();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        setData(null); setErr(false);
        ceoApi.department(dept).then(setData).catch(() => setErr(true));
    }, [dept]);

    if (err) return <div className="py-16"><EmptyState title="Department not found" hint="This department may not be enabled for your company." /></div>;
    if (!data) return <div className="text-neutral-500 py-16 text-center">Loading department…</div>;

    const stat = data.stats;
    return (
        <div data-testid="ceo-department-detail">
            <button onClick={() => nav("/app/ceo")} className="text-[12.5px] text-neutral-500 hover:text-black inline-flex items-center gap-1 mb-4" data-testid="ceo-dept-back">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Back to Command Center
            </button>
            <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-black">{data.label} <span className="text-neutral-400">Department</span></h1>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 my-7">
                {[["Employees", stat.employees], ["Reports", stat.reports], ["Submitted", stat.submitted], ["Files", stat.files], ["Tasks", stat.tasks]].map(([l, v], i) => (
                    <div key={i} className="glass rounded-2xl p-4" data-testid={`ceo-dd-stat-${i}`}>
                        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-neutral-500">{l}</div>
                        <div className="mt-2 font-display text-2xl font-medium text-black">{v}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Block title="Employees">
                    {data.employees.length === 0 ? <Empty /> : data.employees.map((e) => (
                        <Row key={e.id} main={e.name} sub={`${e.email} · ${e.status}`} right={<StatusPill status={e.status} />} testid={`ceo-dd-emp-${e.id}`} />
                    ))}
                </Block>
                <Block title="Recent Reports">
                    {data.recent_reports.length === 0 ? <Empty /> : data.recent_reports.slice(0, 12).map((r) => (
                        <Row key={r.id} main={r.title} sub={`${r.submission_id} · ${r.employee_name} · ${fmtDate(r.updated_at)}`} right={<div className="flex gap-1.5"><PriorityPill priority={r.priority} /><StatusPill status={r.status} /></div>} testid={`ceo-dd-report-${r.id}`} />
                    ))}
                </Block>
                <Block title="Uploaded Files">
                    {data.files.length === 0 ? <Empty /> : data.files.map((f) => (
                        <Row key={f.id} main={f.filename} sub={`${fmtBytes(f.size)} · ${fmtDate(f.uploaded_at)}`} right={<button onClick={async () => { const full = await ceoApi.attachment(f.id); downloadAttachment(full); }} className="text-violet-600 hover:text-violet-700 text-[12px]" data-testid={`ceo-dd-file-${f.id}`}>Download</button>} />
                    ))}
                </Block>
                <Block title="Tasks">
                    {data.tasks.length === 0 ? <Empty /> : data.tasks.slice(0, 12).map((t) => (
                        <Row key={t.id} main={t.title} sub={t.due_date ? `Due ${t.due_date}` : fmtDate(t.created_at)} right={<div className="flex gap-1.5"><PriorityPill priority={t.priority} /><StatusPill status={t.status} /></div>} />
                    ))}
                </Block>
            </div>
        </div>
    );
}

function Block({ title, children }) {
    return (
        <div>
            <h2 className="font-display text-lg font-medium text-black mb-3">{title}</h2>
            <div className="glass rounded-2xl divide-y divide-black/5 overflow-hidden">{children}</div>
        </div>
    );
}
function Row({ main, sub, right, testid }) {
    return (
        <div className="flex items-center justify-between gap-4 px-4 py-3" data-testid={testid}>
            <div className="min-w-0"><div className="text-[13.5px] font-medium text-black truncate">{main}</div><div className="text-[11px] text-neutral-400 truncate">{sub}</div></div>
            <div className="shrink-0">{right}</div>
        </div>
    );
}
function Empty() { return <div className="px-4 py-6 text-[13px] text-neutral-400">Nothing here yet.</div>; }
